import React, { useEffect, useMemo, useCallback, useState } from 'react';
import {
  Box,
  Checkbox,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  OutlinedInput,
} from '@mui/material';
import { useMediaQuery, useTheme } from '@mui/material';
import { Search } from '@mui/icons-material';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { brandColors } from 'types/colors';
import SyncIcon from '@mui/icons-material/Sync';
import DropdownMenuVertical from '../DropdownMenuVertical';
import MarqueeText from './MarqueeText';

import {
  setSelectedBuilding,
  setSelectedCustomer,
  setSelectedDistributor,
  setSelectedFloor,
  setSelectedArea,
  setSelectedHvacEquipment,
  setSelectedStatus,
  setControllerStatus,
  updateFilteredDevicesWithSync,
  updateFilteredControllers,
  clearDeviceUpdateFlag,
  getUsers,
  getBuildings,
} from '../../stores/sensor/sensorStore';
import { getHvacEquipment } from '../../services/hvacEquipmentService';

import { generateFilterBarOptions, convertToLegacyFormat } from '../../helpers/filterHelpers';
import Button from '@mui/material/Button';

// Reusable widths
const W = {
  dropdown: 182,
  building: 240,
  floor: 100,
  searchBtn: 48,
};

const dropdownScrollbarSx = {
  backgroundColor: brandColors.FILTER_BAR,
  scrollbarWidth: 'thin',
  scrollbarColor: `${brandColors.BLUE} ${brandColors.FILTER_BAR}`,
  '&::-webkit-scrollbar': {
    width: 8,
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: brandColors.FILTER_BAR,
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: brandColors.BLUE,
    borderRadius: 8,
    border: `2px solid ${brandColors.FILTER_BAR}`,
  },
};

// Tiny util to avoid anonymous noop recreations
const NOOP = () => {};
const ALL_DEVICES_OPTION = '__all_devices__';

export default function DevicesListFilter({
  type = 'devices', // 'devices' | 'controllers'
  distributor = true,
  customer = true,
  building = true,
  floor = true,
  multiFloorSelect = false,
  selectedFloors = [],
  onFloorsChange = null,
  maxMultiFloorSelections = null,
  hideAllFloorOption = false,
  area = true,
  hvacEquipment = false,
  status = true,
  search = true,
  sensorsSelect = false,
  selectedSensors = [],
  onSensorChange,
  onSelectionChange = null,
  sensorOptions = [],
  groupSensorsByFloor = false,
  showSearchButton = false,
  showRefreshButton = false,
  onSearchClick = null,
  searchButtonDisabled = false,
  onRefreshClick,
  forcedBuildingId = null,
  flexDirection = 'row',
  fullWidth = false,
  // Generic extension point for additional filter controls (dropdowns, date pickers, toggles, etc.).
  // Accepts a single React node or an array of nodes.
  extraFilterFields = null,
  // Kept for backwards compatibility; prefer extraFilterFields for new usages.
  extraDropdownItems = null,
  // Optional desktop widths for fields supplied through the extension points above.
  extraFilterItemWidths = [],
  lgTwoRowLayout = false,
  smMdColumns = 3,
  hvacEquipmentItemSx = {},
}) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isFilterGridScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const isLgScreen = useMediaQuery(theme.breakpoints.only('lg'));
  const useFilterGridLayout = isFilterGridScreen || (lgTwoRowLayout && isLgScreen);
  const effectiveDirection = useFilterGridLayout ? 'row' : flexDirection;
  const [sensorMenuMaxHeight, setSensorMenuMaxHeight] = useState(null);
  const [sensorMenuWidth, setSensorMenuWidth] = useState(null);

  // ---- Select only what you need; keep references stable with shallowEqual ----
  const {
    selectedFilterObject,
    loading,
    searchString: globalSearch,
    shouldUpdateDevices,
  } = useSelector(
    s => ({
      selectedFilterObject: s.persistence?.selectedFilterObject,
      loading: s.persistence?.loading || {},
      searchString: s.persistence?.selectedFilterObject.searchString || '',
      shouldUpdateDevices: s.persistence?.shouldUpdateDevices,
    }),
    shallowEqual,
  );

  const currentUser = useSelector(state => state.sensors.currentUser, shallowEqual);
  const users = useSelector(state => state.users.users || [], shallowEqual);
  const usersLoading = useSelector(state => state.users.loading);
  const buildings = useSelector(state => state.users.buildings || [], shallowEqual);
  const rangeObj = useSelector(state => state.homePage.homePageDatesObj);

  const devices = useSelector(s => (type === 'controllers' ? s.controllers.controllers : s.sensors.devices) || [], shallowEqual);

  const controllerSelectedStatus = useSelector(state => state.controllers.selectedStatus);

  // ---- Destructure filters once ----
  const {
    distributor: selectedDistributor,
    customer: selectedCustomer,
    building: selectedBuilding,
    floor: selectedFloor,
    area: selectedArea,
    hvacEquipment: selectedHvacEquipment,
    status: selectedStatus,
  } = selectedFilterObject || {};
  const normalizedForcedBuildingId =
    forcedBuildingId === null || forcedBuildingId === undefined || forcedBuildingId === '' ? '' : String(forcedBuildingId);
  const selectedBuildingForFilters = normalizedForcedBuildingId || selectedBuilding;
  const [hvacEquipmentOptions, setHvacEquipmentOptions] = useState([]);

  useEffect(() => {
    if (!hvacEquipment || !selectedBuildingForFilters || selectedBuildingForFilters === 'all') {
      setHvacEquipmentOptions([]);
      return;
    }
    let mounted = true;
    getHvacEquipment(selectedBuildingForFilters)
      .then(items => {
        if (!mounted) return;
        setHvacEquipmentOptions([
          { value: 'all', name: 'All' },
          ...items.map(item => ({ value: String(item.id), name: `${item.name} (${item.equipment_type})` })),
        ]);
      })
      .catch(() => {
        if (mounted) setHvacEquipmentOptions([]);
      });
    return () => {
      mounted = false;
    };
  }, [hvacEquipment, type, selectedBuildingForFilters]);

  useEffect(() => {
    if (!normalizedForcedBuildingId) return;
    if (String(selectedBuilding || '') === normalizedForcedBuildingId) return;
    dispatch(setSelectedBuilding(normalizedForcedBuildingId));
  }, [dispatch, normalizedForcedBuildingId, selectedBuilding]);

  // ---- Options (heavy computations) -> memoize ----
  const filterOptions = useMemo(() => {
    return generateFilterBarOptions(
      users,
      currentUser,
      {
        distributor: selectedDistributor,
        customer: selectedCustomer,
        building: selectedBuildingForFilters,
        floor: selectedFloor,
      },
      buildings,
    );
  }, [users, currentUser, selectedDistributor, selectedCustomer, selectedBuildingForFilters, selectedFloor, buildings]);

  // Legacy formats -> memoize to avoid recomputation on every render
  const distributorOptionsLegacy = useMemo(
    () => convertToLegacyFormat(filterOptions.distributors || []),
    [filterOptions.distributors],
  );
  const customerOptionsLegacy = useMemo(() => convertToLegacyFormat(filterOptions.customers || []), [filterOptions.customers]);
  const buildingOptionsLegacy = useMemo(() => convertToLegacyFormat(filterOptions.buildings || []), [filterOptions.buildings]);
  const floorOptionsLegacy = useMemo(
    () =>
      convertToLegacyFormat(filterOptions.floors || []).map(o => ({
        ...o,
        name: o.name.replace(/^floor\s*/i, ''),
      })),
    [filterOptions.floors],
  );
  const floorOptionsForDisplay = useMemo(
    () => (hideAllFloorOption ? floorOptionsLegacy.filter(option => option.value !== 'all') : floorOptionsLegacy),
    [floorOptionsLegacy, hideAllFloorOption],
  );
  const areaOptionsLegacy = useMemo(() => convertToLegacyFormat(filterOptions.areas), [filterOptions.areas]);

  const statusOptionsLegacy = useMemo(
    () =>
      convertToLegacyFormat(
        type === 'controllers'
          ? [
              { value: 'all', label: 'All' },
              { value: '1', label: 'Active' },
              { value: '2', label: 'Inactive' },
            ]
          : [
              { value: 'all', label: 'All' },
              { value: 'active', label: 'Active' },
              { value: '4', label: 'Inactive' },
              { value: '1', label: 'Good' },
              { value: '2', label: 'Moderate' },
              { value: '3', label: 'Bad' },
            ],
      ),
    [type],
  );

  // ---- Initialization ----
  useEffect(() => {
    if (!currentUser?.role) return;
    dispatch(getUsers());
    dispatch(getBuildings());
  }, [dispatch, currentUser?.role]);

  // ---- Auto-select defaults ONLY when nothing chosen ----
  useEffect(() => {
    if (!currentUser?.role || !users?.length || !buildings?.length) return;

    const hasExisting = selectedDistributor || selectedCustomer || selectedBuilding;
    if (hasExisting) return;

    if (currentUser.role === 'd') {
      dispatch(setSelectedDistributor(currentUser.user_id));
    } else if (currentUser.role === 'c') {
      const idStr = String(currentUser.user_id || currentUser.id);
      dispatch(setSelectedCustomer(idStr));
    }
  }, [dispatch, currentUser?.role, users?.length, buildings?.length, selectedDistributor, selectedCustomer, selectedBuilding]);

  // For distributors: if there are customers and none selected -> pick the first (skip "All")
  useEffect(() => {
    if (currentUser?.role !== 'd') return;
    if ((customerOptionsLegacy?.length || 0) <= 1) return;
    if (selectedCustomer && selectedCustomer !== '') return;

    const first = customerOptionsLegacy.find(c => c.value !== 'all');
    if (first) dispatch(setSelectedCustomer(first.value));
  }, [currentUser?.role, customerOptionsLegacy, selectedCustomer, dispatch]);

  // After a customer is selected, optionally auto-select a building (skip "All")
  useEffect(() => {
    if ((buildingOptionsLegacy?.length || 0) <= 1) return;
    if (selectedBuilding && selectedBuilding !== '') return;

    if (type === 'controllers') return;

    const shouldAuto =
      currentUser?.role === 'c' ||
      (currentUser?.role === 'd' && selectedCustomer) ||
      (currentUser?.role === 's' && selectedCustomer);

    if (shouldAuto) {
      const first = buildingOptionsLegacy.find(b => b.value !== 'all');
      if (first) dispatch(setSelectedBuilding(first.value));
    }
  }, [buildingOptionsLegacy, selectedBuilding, selectedCustomer, currentUser?.role, dispatch]);

  // ---- Devices filtering (only when flag says so) ----
  useEffect(() => {
    if (type === 'controllers') return;
    if (!shouldUpdateDevices || !selectedFilterObject) return;

    dispatch(updateFilteredDevicesWithSync(selectedFilterObject));
    dispatch(clearDeviceUpdateFlag());
  }, [type, shouldUpdateDevices, selectedFilterObject, dispatch]);

  // ---- Controllers filtering (dependent only on filters + data presence) ----
  const controllerFilters = useMemo(
    () => ({
      building: selectedBuildingForFilters,
      floor: selectedFloor,
      area: selectedArea,
      status: controllerSelectedStatus,
      searchString: globalSearch,
      distributor: selectedDistributor,
      customer: selectedCustomer,
    }),
    [
      selectedBuildingForFilters,
      selectedFloor,
      selectedArea,
      controllerSelectedStatus,
      globalSearch,
      selectedDistributor,
      selectedCustomer,
    ],
  );

  useEffect(() => {
    if (type !== 'controllers') return;
    if (!devices?.length) return;
    dispatch(updateFilteredControllers({ controllerFilters, currentUser }));
  }, [type, devices?.length, controllerFilters, currentUser, dispatch]);

  // ---- Handlers (stable) ----
  const signalSelectionChange = useCallback(() => onSelectionChange?.(), [onSelectionChange]);

  const handleDistributorChange = useCallback(
    e => {
      const distId = e.target.value;
      dispatch(setSelectedDistributor(distId));
      signalSelectionChange();
    },
    [dispatch, signalSelectionChange],
  );

  const handleCustomerChange = useCallback(
    e => {
      dispatch(setSelectedDistributor('all'));
      dispatch(setSelectedCustomer(e.target.value));
      signalSelectionChange();
    },
    [dispatch, signalSelectionChange],
  );

  const handleBuildingChange = useCallback(
    e => {
      dispatch(setSelectedBuilding(e.target.value));
      signalSelectionChange();
    },
    [dispatch, signalSelectionChange],
  );
  const handleFloorChange = useCallback(
    e => {
      dispatch(setSelectedFloor(e.target.value));
      signalSelectionChange();
    },
    [dispatch, signalSelectionChange],
  );
  const handleAreaChange = useCallback(
    e => {
      dispatch(setSelectedArea(e.target.value));
      signalSelectionChange();
    },
    [dispatch, signalSelectionChange],
  );

  const handleStatusChange = useCallback(
    e => {
      const v = e.target.value;
      if (type === 'controllers') dispatch(setControllerStatus(v));
      else dispatch(setSelectedStatus(v));
      signalSelectionChange();
    },
    [dispatch, signalSelectionChange, type],
  );

  // Debounce search so we don't dispatch on every keystroke
  const [localSearch, setLocalSearch] = useState(globalSearch || '');

  useEffect(() => setLocalSearch(globalSearch || ''), [globalSearch]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (localSearch !== globalSearch) {
        // reuse the same action used in your store
        dispatch({ type: 'persistence/setSearchString', payload: localSearch });
      }
    }, 300);
    return () => clearTimeout(t);
  }, [localSearch, globalSearch, dispatch]);

  const onSearch = useMemo(() => onSearchClick || NOOP, [onSearchClick]);
  const onFloorsSelect = useMemo(() => onFloorsChange || NOOP, [onFloorsChange]);
  const selectedFloorValues = useMemo(
    () => (Array.isArray(selectedFloors) ? selectedFloors.map(String).filter(v => v && v !== 'all') : []),
    [selectedFloors],
  );

  const handleFloorMultiChange = useCallback(
    e => {
      const rawValue = e.target.value;
      const nextValues = (Array.isArray(rawValue) ? rawValue : [rawValue]).map(String);

      const uniqueValues = Array.from(new Set(nextValues.filter(v => v !== 'all')));
      const maxLimit = Number.isInteger(maxMultiFloorSelections) && maxMultiFloorSelections > 0 ? maxMultiFloorSelections : null;

      onFloorsSelect(maxLimit ? uniqueValues.slice(0, maxLimit) : uniqueValues);
      signalSelectionChange();
    },
    [onFloorsSelect, maxMultiFloorSelections, signalSelectionChange],
  );
  const hasReachedMultiFloorLimit =
    Number.isInteger(maxMultiFloorSelections) &&
    maxMultiFloorSelections > 0 &&
    selectedFloorValues.length >= maxMultiFloorSelections;

  // ---- Visibility / disabled logic ----
  const showDistributor = currentUser?.role === 's' && distributor;
  const showCustomer = (currentUser?.role === 's' || currentUser?.role === 'd') && customer;

  const distributorDisabled = loading.distributors || usersLoading;
  const customerDisabled =
    loading.customers || usersLoading || (currentUser?.role === 's' && (!selectedDistributor || selectedDistributor === ''));

  const buildingDisabled =
    loading.buildings ||
    usersLoading ||
    (buildingOptionsLegacy?.length || 0) === 0 ||
    (currentUser?.role === 's' && (!selectedCustomer || selectedCustomer === ''));

  const floorDisabled =
    !selectedBuildingForFilters || selectedBuildingForFilters === 'all' || (floorOptionsLegacy?.length || 0) <= 1;
  const areaDisabled =
    !selectedFloor || selectedFloor === 'all' || !selectedBuildingForFilters || selectedBuildingForFilters === 'all';

  const additionalFilterFields = useMemo(
    () => React.Children.toArray([extraDropdownItems, extraFilterFields]),
    [extraDropdownItems, extraFilterFields],
  );
  const groupedSensorOptions = useMemo(() => {
    if (!groupSensorsByFloor) return [];

    return sensorOptions
      .reduce((groups, sensor) => {
        const floorNumber = sensor.floorNumber ?? 'Unassigned';
        const existingGroup = groups.find(group => group.floorNumber === floorNumber);

        if (existingGroup) {
          existingGroup.sensors.push(sensor);
        } else {
          groups.push({ floorNumber, sensors: [sensor] });
        }

        return groups;
      }, [])
      .sort((a, b) => {
        if (a.floorNumber === 'Unassigned') return 1;
        if (b.floorNumber === 'Unassigned') return -1;
        return Number(a.floorNumber) - Number(b.floorNumber);
      });
  }, [groupSensorsByFloor, sensorOptions]);
  const sensorOptionIds = useMemo(() => sensorOptions.map(sensor => sensor.id), [sensorOptions]);
  const allSensorOptionsSelected =
    sensorOptionIds.length > 0 && sensorOptionIds.every(sensorId => selectedSensors.includes(sensorId));
  const onSensorSelect = useMemo(() => onSensorChange || NOOP, [onSensorChange]);
  const toggleAllSensorOptions = useCallback(() => {
    onSensorSelect(allSensorOptionsSelected ? [] : sensorOptionIds);
    signalSelectionChange();
  }, [allSensorOptionsSelected, onSensorSelect, sensorOptionIds, signalSelectionChange]);
  const toggleSensorOption = useCallback(
    sensorId => {
      onSensorSelect(
        selectedSensors.includes(sensorId)
          ? selectedSensors.filter(selectedId => selectedId !== sensorId)
          : [...selectedSensors, sensorId],
      );
      signalSelectionChange();
    },
    [onSensorSelect, selectedSensors, signalSelectionChange],
  );
  const extraFilterFieldCount = additionalFilterFields.length;
  const responsiveFieldCount =
    Number(showDistributor) +
    Number(showCustomer) +
    Number(building) +
    Number(floor) +
    Number(area) +
    Number(hvacEquipment) +
    Number(status) +
    extraFilterFieldCount +
    Number(search) +
    Number(sensorsSelect);

  let responsiveFieldIndex = 0;
  const smMdColumnWidth = smMdColumns === 2 ? '50%' : '33.333333%';
  const lgColumnWidth = `${100 / Math.ceil(responsiveFieldCount / 2)}%`;
  const getFilterItemWidth = desktopWidth => {
    const fieldIndex = responsiveFieldIndex;
    responsiveFieldIndex += 1;

    const isLastField = fieldIndex === responsiveFieldCount - 1;
    const isLastFieldAloneOnXsRow = isLastField && responsiveFieldCount % 2 === 1;

    return useFilterGridLayout
      ? {
          xs: isLastFieldAloneOnXsRow ? '100%' : '50%',
          md: smMdColumnWidth,
          ...(lgTwoRowLayout ? { lg: lgColumnWidth } : {}),
        }
      : effectiveDirection === 'row'
        ? desktopWidth
        : '100%';
  };

  const getDropdownControlWidth = desktopWidth => (useFilterGridLayout || effectiveDirection !== 'row' ? '100%' : desktopWidth);

  // ---- Render ----
  return (
    <Box
      sx={{
        width: fullWidth || useFilterGridLayout || effectiveDirection !== 'row' ? '100%' : null,
        maxWidth: '100%',
      }}>
      <Grid
        container
        spacing={1}
        sx={{
          flexWrap: useFilterGridLayout || effectiveDirection !== 'row' ? 'wrap' : 'nowrap',
          alignItems: 'flex-start',
          flexDirection: effectiveDirection,
          width: fullWidth || useFilterGridLayout || effectiveDirection !== 'row' ? '100%' : null,
          marginLeft: '-5px',
        }}>
        {showDistributor && (
          <Grid item sx={{ width: getFilterItemWidth(W.dropdown) }}>
            <Box py={1}>
              <DropdownMenuVertical
                disabled={distributorDisabled}
                options={distributorOptionsLegacy}
                handleChange={handleDistributorChange}
                selectedOption={selectedDistributor || 'all'}
                labelName="Distributor"
                placeholder="Select Distributor"
                menuScrollbarSx={dropdownScrollbarSx}
              />
            </Box>
          </Grid>
        )}

        {showCustomer && (
          <Grid item sx={{ width: getFilterItemWidth(W.dropdown) }}>
            <Box py={1}>
              <DropdownMenuVertical
                disabled={customerDisabled}
                options={customerOptionsLegacy}
                handleChange={handleCustomerChange}
                selectedOption={selectedCustomer || 'all'}
                labelName="Customer"
                placeholder="Select Customer"
                menuScrollbarSx={dropdownScrollbarSx}
              />
            </Box>
          </Grid>
        )}

        {building && (
          <Grid item sx={{ width: getFilterItemWidth(W.building) }}>
            <Box py={1}>
              <DropdownMenuVertical
                disabled={buildingDisabled}
                options={buildingOptionsLegacy}
                handleChange={handleBuildingChange}
                selectedOption={selectedBuilding || 'all'}
                labelName="Building"
                placeholder="Select Building"
                menuScrollbarSx={dropdownScrollbarSx}
              />
            </Box>
          </Grid>
        )}

        {floor && (
          <Grid
            item
            sx={{
              width: getFilterItemWidth(multiFloorSelect ? W.dropdown : W.floor),
            }}>
            <Box py={1}>
              {multiFloorSelect ? (
                <FormControl sx={{ width: getDropdownControlWidth(W.dropdown) }}>
                  <InputLabel
                    id="floor-multi-select-label"
                    shrink
                    sx={{
                      backgroundColor: '#ffffff00',
                      px: 0.5,
                    }}>
                    Floor
                  </InputLabel>
                  <Select
                    labelId="floor-multi-select-label"
                    multiple
                    value={selectedFloorValues}
                    onChange={handleFloorMultiChange}
                    input={<OutlinedInput label="Floor" />}
                    disabled={floorDisabled}
                    displayEmpty
                    MenuProps={{
                      disableScrollLock: true,
                      PaperProps: {
                        sx: {
                          marginTop: '-5px',
                          ...dropdownScrollbarSx,
                        },
                      },
                    }}
                    renderValue={selected => {
                      if (!selected?.length) return '';
                      return floorOptionsForDisplay
                        .filter(o => selected.includes(String(o.value)))
                        .map(o => o.name)
                        .join(', ');
                    }}>
                    {floorOptionsForDisplay.map(option => (
                      <MenuItem
                        key={option.value}
                        value={option.value}
                        disabled={hasReachedMultiFloorLimit && !selectedFloorValues.includes(String(option.value))}
                        sx={{
                          paddingLeft: 0,
                          backgroundColor: '#fff !important',
                          '&:hover': { backgroundColor: '#e3e3e3 !important' },
                        }}>
                        <Checkbox checked={selectedFloorValues.includes(String(option.value))} />
                        <ListItemText primary={option.name} primaryTypographyProps={{ sx: { fontSize: '0.9rem' } }} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <DropdownMenuVertical
                  disabled={floorDisabled}
                  options={floorOptionsForDisplay}
                  handleChange={handleFloorChange}
                  selectedOption={
                    hideAllFloorOption
                      ? selectedFloor && selectedFloor !== 'all'
                        ? selectedFloor
                        : floorOptionsForDisplay[0]?.value || ''
                      : selectedFloor || 'all'
                  }
                  labelName="Floor"
                  placeholder={hideAllFloorOption ? 'Select Floor' : 'All'}
                  menuScrollbarSx={dropdownScrollbarSx}
                />
              )}
            </Box>
          </Grid>
        )}

        {area && (
          <Grid item sx={{ width: getFilterItemWidth(W.dropdown) }}>
            <Box py={1}>
              <DropdownMenuVertical
                disabled={areaDisabled}
                options={filterOptions.areas}
                handleChange={handleAreaChange}
                selectedOption={selectedArea || 'all'}
                labelName="Area"
                placeholder="All Areas"
                menuScrollbarSx={dropdownScrollbarSx}
              />
            </Box>
          </Grid>
        )}

        {hvacEquipment && (
          <Grid item sx={{ width: getFilterItemWidth(W.dropdown), ...hvacEquipmentItemSx }}>
            <Box py={1}>
              <DropdownMenuVertical
                disabled={!selectedBuildingForFilters || selectedBuildingForFilters === 'all'}
                options={hvacEquipmentOptions}
                handleChange={event => dispatch(setSelectedHvacEquipment(event.target.value))}
                selectedOption={selectedHvacEquipment || 'all'}
                labelName="HVAC equipment"
                emptyDisplayValue="All"
                menuScrollbarSx={dropdownScrollbarSx}
              />
            </Box>
          </Grid>
        )}

        {status && (
          <Grid item sx={{ width: getFilterItemWidth(140) }}>
            <Box py={1}>
              <DropdownMenuVertical
                options={statusOptionsLegacy}
                handleChange={handleStatusChange}
                selectedOption={type === 'controllers' ? controllerSelectedStatus : selectedStatus || 'all'}
                labelName="Status"
                menuScrollbarSx={dropdownScrollbarSx}
              />
            </Box>
          </Grid>
        )}

        {search && (
          <Grid
            item
            sx={{ width: getFilterItemWidth(undefined) }}
            xs={!useFilterGridLayout && effectiveDirection === 'row' ? 4 : undefined}
            sm={!useFilterGridLayout && effectiveDirection === 'row' ? 3 : undefined}
            md={!useFilterGridLayout && effectiveDirection === 'row' ? 1.5 : undefined}>
            <Box py={1} sx={{ width: '100%' }}>
              <TextField
                id="outlined-search"
                label={`Search ${type}`}
                variant="outlined"
                value={localSearch}
                onChange={e => setLocalSearch(e.target.value)}
                sx={{ width: '100%' }}
              />
            </Box>
          </Grid>
        )}

        {additionalFilterFields.map((field, index) => (
          <Grid
            item
            key={field.key || `extra-filter-field-${index}`}
            sx={{ width: getFilterItemWidth(extraFilterItemWidths[index] || W.dropdown) }}>
            <Box py={1}>{field}</Box>
          </Grid>
        ))}

        {sensorsSelect && (
          <Grid item sx={{ width: getFilterItemWidth(W.dropdown) }}>
            <Box py={1}>
              <FormControl sx={{ width: getDropdownControlWidth(W.dropdown) }}>
                <InputLabel id="devices-label" shrink={selectedBuilding !== 'all'}>
                  {/* {selectedSensors.length}/{sensorOptions.length} */}
                </InputLabel>{' '}
                <Select
                  labelId="devices-label"
                  label="Devices"
                  multiple
                  displayEmpty
                  value={selectedSensors}
                  onChange={NOOP}
                  onOpen={event => {
                    const selectElement = event.currentTarget.closest('.MuiInputBase-root') || event.currentTarget;
                    setSensorMenuMaxHeight(Math.max(0, window.innerHeight - selectElement.getBoundingClientRect().bottom - 20));
                    setSensorMenuWidth(selectElement.getBoundingClientRect().width);
                  }}
                  input={<OutlinedInput notched={false} />}
                  renderValue={() => `${selectedSensors.length}/${sensorOptions.length} devices`}
                  MenuProps={{
                    disableScrollLock: true,
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                    PaperProps: {
                      sx: {
                        width: sensorMenuWidth || undefined,
                        minWidth: sensorMenuWidth || undefined,
                        maxWidth: sensorMenuWidth || undefined,
                        maxHeight: sensorMenuMaxHeight,
                        backgroundColor: brandColors.FILTER_BAR,
                        marginTop: '-6px',
                        boxShadow: '0 12px 24px -12px rgba(0,0,0,0.35), 0 4px 0px -6px rgba(0,0,0,0.25)',
                        scrollbarWidth: 'thin',
                        scrollbarColor: `${brandColors.BLUE} ${brandColors.FILTER_BAR}`,
                        '&::-webkit-scrollbar': {
                          width: 8,
                        },
                        '&::-webkit-scrollbar-track': {
                          backgroundColor: brandColors.FILTER_BAR,
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: brandColors.BLUE,
                          borderRadius: 8,
                          border: `2px solid ${brandColors.FILTER_BAR}`,
                        },
                      },
                    },
                  }}>
                  <MenuItem
                    sx={{ paddingLeft: 0, backgroundColor: '#fff !important' }}
                    key={ALL_DEVICES_OPTION}
                    value={ALL_DEVICES_OPTION}
                    onClick={toggleAllSensorOptions}>
                    <Checkbox checked={allSensorOptionsSelected} />
                    <ListItemText primary="All" primaryTypographyProps={{ sx: { fontSize: '0.9rem' } }} />
                  </MenuItem>
                  {groupSensorsByFloor
                    ? groupedSensorOptions.map(group => (
                        <React.Fragment key={`floor-${group.floorNumber}`}>
                          <MenuItem
                            disabled
                            sx={{
                              minHeight: 0,
                              py: 0.5,
                              px: 2,
                              backgroundColor: `${brandColors.FILTER_BAR} !important`,
                              fontSize: '0.8rem',
                              fontWeight: 700,
                              opacity: '1 !important',
                            }}>
                            Floor {group.floorNumber}
                          </MenuItem>
                          {group.sensors.map(sensorObj => (
                            <MenuItem
                              key={sensorObj.id}
                              value={sensorObj.id}
                              onClick={() => toggleSensorOption(sensorObj.id)}
                              sx={{
                                paddingLeft: 0,
                                backgroundColor: '#fff !important',
                                '&:hover': { backgroundColor: '#e3e3e3 !important' },
                              }}>
                              <Checkbox checked={selectedSensors.includes(sensorObj.id)} />
                              <ListItemText
                                sx={{ minWidth: 0, overflow: 'hidden' }}
                                primary={<MarqueeText>{sensorObj.name}</MarqueeText>}
                              />
                            </MenuItem>
                          ))}
                        </React.Fragment>
                      ))
                    : sensorOptions.map(sensorObj => (
                        <MenuItem
                          key={sensorObj.id}
                          value={sensorObj.id}
                          onClick={() => toggleSensorOption(sensorObj.id)}
                          sx={{
                            paddingLeft: 0,
                            backgroundColor: '#fff !important',
                            '&:hover': { backgroundColor: '#e3e3e3 !important' },
                          }}>
                          <Checkbox checked={selectedSensors.includes(sensorObj.id)} />
                          <ListItemText
                            sx={{ minWidth: 0, overflow: 'hidden' }}
                            primary={<MarqueeText>{sensorObj.name}</MarqueeText>}
                          />
                        </MenuItem>
                      ))}
                </Select>
              </FormControl>
            </Box>
          </Grid>
        )}

        {showSearchButton && (
          <Grid item>
            <Box py={1}>
              <IconButton
                onClick={onSearch}
                disabled={searchButtonDisabled}
                sx={{
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  color: brandColors.BLUE,
                  height: 3,
                  width: 3,
                  marginLeft: '1rem',
                  marginTop: '.8rem',
                  scale: '1.2',
                  '&:hover': {},
                }}>
                <Search />
              </IconButton>
            </Box>
          </Grid>
        )}

        {/* ===== SEARCH/REFRESH BUTTON ===== */}
        {showRefreshButton && (
          <Grid>
            <Button
              sx={{
                backgroundColor: brandColors.LIGHT_BLUE,
                color: '#000',
                height: '3rem',
                marginTop: '7px',
                marginLeft: '7px',
                border: `1px solid ${brandColors.BLUE}`,
                '&:hover': {
                  backgroundColor: `${brandColors.BLUE}`,
                  color: '#fff',
                },
              }}
              variant="gradient"
              onClick={onRefreshClick}
              disabled={false}>
              <SyncIcon color="inherit" fontSize="large">
                sync
              </SyncIcon>
            </Button>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
